import {
  Component,
  ChangeDetectionStrategy,
  forwardRef,
  inject,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SafetyCertificate } from '@wilson/interfaces';
import { SafetyCertificateService } from '@wilson/safety-certificate';
import { Observable, of } from 'rxjs';

@Component({
  selector: 'wilson-safety-certificate-autocomplete',
  templateUrl: './safety-certificate-autocomplete.component.html',
  styleUrls: ['./safety-certificate-autocomplete.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SafetyCertificateAutocompleteComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class SafetyCertificateAutocompleteComponent
  implements ControlValueAccessor
{
  private safetyCertificateService = inject(SafetyCertificateService);

  public value: SafetyCertificate | null = null;

  public certificates$: Observable<SafetyCertificate[]> = of([]);

  private onChange: (value: SafetyCertificate | null) => void = () => {
    //This block is intentionally empty
  };

  private onTouch: (value: SafetyCertificate | null) => void = () => {
    //This block is intentionally empty
  };

  private setValue(val: SafetyCertificate | null) {
    this.value = val;
    this.onChange(val);
    this.onTouch(val);
  }

  setCertificate(certificate: SafetyCertificate) {
    const val = !certificate?.name ? null : certificate;
    this.setValue(val);
  }

  // this method sets the value programmatically
  writeValue(value: SafetyCertificate | null) {
    this.setValue(value);
  }
  // upon UI element value changes, this method gets triggered
  registerOnChange(fn: (value: SafetyCertificate | null) => void) {
    this.onChange = fn;
  }
  // upon touching the element, this method gets triggered
  registerOnTouched(fn: (value: SafetyCertificate | null) => void) {
    this.onTouch = fn;
  }

  changeCertificateSuggestions(searchTermEvent: Event) {
    const searchTerm = (searchTermEvent.target as HTMLInputElement).value;
    if (searchTerm?.length > 2) {
      this.certificates$ = this.safetyCertificateService.getAll({
        where: {
          name: searchTerm,
        },
        limit: 25,
      });
    }
  }

  clearValue() {
    if (!this.value?.name) {
      this.value = { name: '' };
    }
  }
}
